import React from "react";
import { Table, Input, Tag, Tooltip, Typography } from "antd";
import { faList, faBagShopping, faStore, faMobileScreen } from "@fortawesome/free-solid-svg-icons";
import { fmtUnix, defaultRange } from "./log-utils";
import { useLogTable } from "./hooks/use-log-table";
import LogStatCards from "./components/log-stat-cards";
import LogToolbar from "./components/log-toolbar";
import LogFilterSelect from "./components/log-filter-select";
import LogIpFilter from "./components/log-ip-filter";
import LogTimelineChart from "./components/log-timeline-chart";
import LogDetailDrawer from "./components/log-detail-drawer";
import logApi from "../../../api/log-api";

const { Text } = Typography;

// shopeefood là sàn thứ 4 đang có click thật — trước đây thiếu hẳn ở đây.
const PLATFORM_COLOR = {
    shopee: "orange", lazada: "blue", tiktok: "purple", shopeefood: "red",
};

// Tab xem ClickLog — mỗi lần tạo link affiliate (xương sống đối soát hoa hồng).
// Stat card phản ánh đúng bộ lọc hiện tại (kể cả khoảng ngày).
// options = { platform, channel, source } lấy từ DB, do trang cha truyền xuống.
function ClickLogTab({ onRefreshStats, options }) {
    const t = useLogTable({
        name: "ClickLog",
        initialFilter: {
            platform: "", channel: "", source: "", os: "", userId: "", clickId: "", ip: "",
            ...defaultRange(),
        },
        load: (p) => logApi.getClicks(p),
        remove: (ids) => logApi.deleteClicks(ids),
        removeAll: (f) => logApi.deleteAllClicks(f),
        loadStats: (f) => logApi.getClickStats(f),
        exportAll: (f) => logApi.exportCsv("clicks", f),
        onMutated: onRefreshStats,
    });

    const s = t.stats;
    const cards = [
        { key: "total", title: "Tổng click", value: s?.total, icon: faList, iconColor: "text-blue-600", bgColor: "bg-blue-50" },
        { key: "shopee", title: "Shopee", value: s?.shopee, icon: faBagShopping, iconColor: "text-orange-500", bgColor: "bg-orange-50" },
        { key: "lazada", title: "Lazada", value: s?.lazada, icon: faStore, iconColor: "text-blue-600", bgColor: "bg-blue-50" },
        { key: "shopeefood", title: "ShopeeFood", value: s?.shopeefood, icon: faStore, iconColor: "text-red-600", bgColor: "bg-red-50" },
        { key: "app", title: "Từ App", value: s?.app, icon: faMobileScreen, iconColor: "text-green-600", bgColor: "bg-green-50" },
    ];

    const columns = [
        { title: "Thời gian", dataIndex: "created_at", width: 150, render: fmtUnix },
        {
            title: "clickId", dataIndex: "clickId", width: 150,
            render: (v) => v ? <Text copyable={{ text: v }} style={{ fontSize: 12 }}>{v}</Text> : "-",
        },
        {
            title: "Platform", dataIndex: "platform", width: 100,
            render: (p) => p ? <Tag color={PLATFORM_COLOR[p] || "default"}>{p}</Tag> : "-",
        },
        { title: "Kênh", dataIndex: "channel", width: 80 },
        { title: "Source", dataIndex: "source", width: 90 },
        { title: "User", dataIndex: "userId", width: 90 },
        { title: "OS", dataIndex: "os", width: 80 },
        { title: "App ver", dataIndex: "appVersion", width: 90 },
        {
            title: "Target URL", dataIndex: "targetUrl", ellipsis: true,
            render: (u) => u ? <Tooltip title={u}><a href={u} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{u}</a></Tooltip> : "-",
        },
        { title: "IP", dataIndex: "ip", width: 130 },
    ];

    return (
        <div>
            <LogStatCards cards={cards} loading={t.statsLoading} />
            <LogToolbar t={t} onRefresh={() => { t.reload(); onRefreshStats?.(); }}>
                <LogFilterSelect
                    width={130} placeholder="Platform" values={options?.platform}
                    value={t.f.platform} onChange={(v) => t.setField("platform", v)}
                />
                <LogFilterSelect
                    width={100} placeholder="Kênh" values={options?.channel}
                    value={t.f.channel} onChange={(v) => t.setField("channel", v)}
                />
                <LogFilterSelect
                    width={110} placeholder="Source" values={options?.source}
                    value={t.f.source} onChange={(v) => t.setField("source", v)}
                />
                <LogFilterSelect
                    width={100} placeholder="OS" values={options?.os}
                    value={t.f.os} onChange={(v) => t.setField("os", v)}
                />
                <Input style={{ width: 100 }} placeholder="User ID" allowClear
                    inputMode="numeric" value={t.f.userId}
                    onChange={(e) => t.setField("userId", e.target.value.replace(/\D/g, ""))} />
                <Input.Search style={{ width: 180 }} placeholder="clickId" allowClear value={t.f.clickId}
                    onChange={(e) => t.setField("clickId", e.target.value)} onSearch={t.search} />
                <LogIpFilter
                    value={t.f.ip}
                    onChange={(v) => t.setField("ip", v)}
                    onSearch={t.search}
                />
            </LogToolbar>

            <LogTimelineChart type="clicks" filter={t.appliedF} reloadKey={t.version} color="#fa8c16" />

            <Table
                rowKey="id"
                size="small"
                loading={t.loading}
                columns={columns}
                dataSource={t.rows}
                scroll={{ x: 1200 }}
                rowSelection={{ selectedRowKeys: t.selected, onChange: t.setSelected }}
                onRow={(r) => ({ onClick: () => t.openDetail(r), style: { cursor: "pointer" } })}
                pagination={{
                    current: t.page, pageSize: t.limit, total: t.total,
                    showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200],
                    showTotal: (n) => `${n} dòng`,
                    onChange: t.onPageChange, onShowSizeChange: t.onPageChange,
                }}
            />

            <LogDetailDrawer row={t.detail} title="Chi tiết click" onClose={t.closeDetail} />
        </div>
    );
}

export default ClickLogTab;
